/**
 * Horizontal space actually available to floating UI — the viewport minus the
 * docked Velo rail.
 *
 * The rail (`AiAssistantDrawer`, 340px, `z-10000`) publishes `--ai-rail-width`
 * and `index.css` derives `--ai-rail-push` from it behind a `min-width: 640px`
 * query. Overlays are supposed to offset themselves with that var, but the
 * usual `sm:pr-[var(--ai-rail-push)]` on a full-viewport container is INERT for
 * an out-of-flow child: for `position: absolute` the containing block is the
 * padding box (padding does not move its edges), and for `position: fixed` it is
 * the viewport (the parent is irrelevant entirely). Anything that clamps against
 * `window.innerWidth` therefore happily positions itself underneath the rail.
 *
 * Use `appAreaWidth()` in place of `window.innerWidth` whenever the number feeds
 * a horizontal position or clamp. Constant offsets do not need this — write them
 * as `calc(<const> + var(--ai-rail-push))` in CSS instead, which also inherits
 * the rail's 250ms transition for free.
 *
 * Deliberately reads the CSS variable rather than the assistant provider:
 * `useAiAssistantOptional` returns null in template-mode builders and otherwise
 * resolves to the NEAREST provider, which inside every builder is a
 * `chrome={false}` scope — it would report "open" for a rail that is not there.
 */
export function railInset(): number {
  if (typeof window === "undefined") return 0;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    "--ai-rail-push",
  );
  const px = parseFloat(raw);
  return Number.isFinite(px) ? px : 0;
}

/** `window.innerWidth` minus the docked rail. */
export function appAreaWidth(): number {
  if (typeof window === "undefined") return 0;
  return window.innerWidth - railInset();
}

/**
 * Offset between the two coordinate frames a floating box has to live in.
 *
 * `getBoundingClientRect()` measures in one frame; a `position: fixed` box is
 * laid out in another. On every desktop, and on a phone with no keyboard, the
 * two are the same and this is zero.
 *
 * With the iOS keyboard up they come apart: fixed boxes stop clinging to the
 * viewport and anchor to the DOCUMENT instead. Measured on an iPhone with a
 * picker open — a probe element at `position: fixed; top: 0` reported a rect
 * top of **-295**, with `scrollY` at exactly 295. Anything computed from a rect
 * and emitted as a fixed coordinate is off by that much, which is the gap that
 * appeared above dropdowns.
 */
export function fixedFrameOffset(): { x: number; y: number } {
  if (typeof window === "undefined") return { x: 0, y: 0 };
  return { x: window.scrollX, y: window.scrollY };
}

/**
 * The part of the screen that is actually VISIBLE, in the frame
 * `getBoundingClientRect()` speaks — so it can be compared against anchor rects
 * without a conversion at every call site.
 *
 * On iOS the software keyboard does not simply shorten the page: it shrinks the
 * visual viewport AND scrolls the document underneath. `visualViewport.offsetTop`
 * is reported against the document, so it has to come back through
 * {@link fixedFrameOffset} to line up with a rect. Same measurement as above:
 * offsetTop 295 with scrollY 295 means the visible band starts at rect 0, not
 * at rect 295 — and reading it as 295 told the placement there were 307px below
 * a field that had 12.
 */
export function visibleBand(): { top: number; bottom: number; height: number } {
  if (typeof window === "undefined") return { top: 0, bottom: 0, height: 0 };
  const vv = window.visualViewport;
  if (!vv) {
    return { top: 0, bottom: window.innerHeight, height: window.innerHeight };
  }
  const top = vv.offsetTop - fixedFrameOffset().y;
  return { top, bottom: top + vv.height, height: vv.height };
}

/** Tallest an anchored dropdown is ever allowed to be. */
export const DROPDOWN_MAX_HEIGHT = 240;
/** Breathing room the dropdown keeps from the edge of the visible band. */
export const DROPDOWN_EDGE = 16;

/**
 * How tall an anchored dropdown may be so that it stays inside the visible
 * band. Both arguments are rect coordinates — the anchor edge the list grows
 * away from, and the band from {@link visibleBand}.
 *
 * There is deliberately no minimum. The rule this replaced floored the height
 * at 100px even when less room existed, which is a floor that can OVERRIDE the
 * space available: with 44px above the field the list still asked for 100 and
 * took the rest from outside the screen. A list too short to be pleasant still
 * scrolls; one that runs off the screen does not.
 */
export function dropdownMaxHeight(
  anchorEdge: number,
  showAbove: boolean,
  band: { top: number; bottom: number },
  /**
   * The tallest the box wants to be. Defaults to the dropdown cap; a PANEL
   * (a dialog anchored to a cell, not a list) passes its own measured height,
   * because 240px is a cap for option lists and a panel that is cut at 240
   * scrolls even on a screen with room for all of it.
   */
  limit: number = DROPDOWN_MAX_HEIGHT,
): number {
  const available = showAbove
    ? anchorEdge - band.top - DROPDOWN_EDGE
    : band.bottom - anchorEdge - DROPDOWN_EDGE;
  return Math.max(0, Math.min(limit, available));
}
